/**
 * Diario di salute / readiness dell'atleta.
 *
 * Form giornaliero separato (non legato al workout), attivabile dall'atleta o
 * dal coach. Prima della prima registrazione l'atleta accetta un consenso
 * informato (dati sanitari, GDPR art. 9); tutti i campi sono facoltativi.
 * Mobile-first: colonna singola, target ampi, pochi tap.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { fetchAPI, getToday } from "../lib/utils";
import { WELLNESS_SCALE_OPTIONS, formatWellnessValue } from "../lib/wellness";
import { WellnessCharts } from "../components/wellness/WellnessCharts";
import { WellnessFieldPicker } from "../components/wellness/WellnessFieldPicker";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import {
	HeartPulse,
	Loader2,
	ShieldCheck,
	Check,
	Trash2,
	PencilLine,
	BarChart3,
	ClipboardList,
	CheckCircle2,
} from "lucide-react";

export default function WellnessDiary() {
	const navigate = useNavigate();
	const { language, t } = useLanguage();
	const { checkAuth } = useAuth();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [accepting, setAccepting] = useState(false);
	const [activating, setActivating] = useState(false);
	const [catalog, setCatalog] = useState([]);
	const [selectedFields, setSelectedFields] = useState([]);
	const [consentText, setConsentText] = useState("");
	const [config, setConfig] = useState(null);
	const [entries, setEntries] = useState([]);
	const [date, setDate] = useState(getToday());
	const [values, setValues] = useState({});
	const [view, setView] = useState("form");
	const [deleting, setDeleting] = useState(null);

	const activeFields = useMemo(() => {
		const selected = new Set(config?.fields || []);
		return (catalog || []).filter((field) => selected.has(field.key));
	}, [catalog, config]);

	const loadAll = useCallback(async () => {
		setLoading(true);
		try {
			const [catalogData, configData, entriesData] = await Promise.all([
				fetchAPI(`/wellness/catalog?language=${language}`).catch(() => null),
				fetchAPI("/wellness/me/config").catch(() => null),
				fetchAPI("/wellness/me/entries").catch(() => []),
			]);
			setCatalog(catalogData?.fields || []);
			setConsentText(catalogData?.consent?.text || "");
			setConfig(configData || { enabled: false, fields: [] });
			setSelectedFields(
				configData?.fields?.length
					? configData.fields
					: catalogData?.default_fields || [],
			);
			setEntries(Array.isArray(entriesData) ? entriesData : []);
		} catch {
			toast.error(t("wellnessDiaryLoadError"));
		} finally {
			setLoading(false);
		}
	}, [language, t]);

	useEffect(() => {
		loadAll();
	}, [loadAll]);

	// Prefill the form with the selected day's existing entry (if any).
	useEffect(() => {
		const existing = entries.find((entry) => entry.date === date);
		setValues(existing?.values ? { ...existing.values } : {});
	}, [date, entries]);

	const setValue = (key, value) =>
		setValues((prev) => ({ ...prev, [key]: value }));

	const toggleSelectedField = (key) => {
		setSelectedFields((prev) =>
			prev.includes(key)
				? prev.filter((field) => field !== key)
				: [...prev, key],
		);
	};

	const handleActivate = async () => {
		setActivating(true);
		try {
			const updated = await fetchAPI("/wellness/me/config", {
				method: "PUT",
				body: JSON.stringify({
					enabled: true,
					fields: selectedFields,
					reminder_enabled: true,
				}),
			});
			setConfig(updated);
			window.dispatchEvent(new Event("wellness-updated"));
			checkAuth();
		} catch {
			toast.error(t("wellnessActivationError"));
		} finally {
			setActivating(false);
		}
	};

	// A day counts as "filled" when its entry holds at least one real value.
	const entryHasData = (entry) =>
		Object.values(entry?.values || {}).some(
			(v) => v !== null && v !== undefined && v !== "",
		);

	const filledDates = useMemo(() => {
		const set = new Set();
		for (const entry of entries) {
			if (entryHasData(entry)) set.add(entry.date);
		}
		return set;
	}, [entries]);

	const selectedDayFilled = filledDates.has(date);
	const isToday = date === getToday();

	const handleAcceptConsent = async () => {
		setAccepting(true);
		try {
			const updated = await fetchAPI("/wellness/me/consent", {
				method: "POST",
			});
			setConfig(updated);
			toast.success(t("wellnessConsentRecorded"));
			window.dispatchEvent(new Event("wellness-updated"));
		} catch {
			toast.error(t("wellnessConsentSaveError"));
		} finally {
			setAccepting(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const saved = await fetchAPI(`/wellness/me/entries/${date}`, {
				method: "PUT",
				body: JSON.stringify({ values }),
			});
			setEntries((prev) => {
				const rest = prev.filter((entry) => entry.date !== date);
				return [...rest, saved].sort((a, b) =>
					String(a.date).localeCompare(String(b.date)),
				);
			});
			toast.success(t("wellnessDataSaved"));
			window.dispatchEvent(new Event("wellness-updated"));
		} catch {
			toast.error(t("wellnessSaveError"));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteDay = async (targetDate) => {
		if (
			!window.confirm(
				t("wellnessDeleteDayConfirm").replace("{date}", targetDate),
			)
		)
			return;
		setDeleting(targetDate);
		try {
			await fetchAPI(`/wellness/me/entries/${targetDate}`, {
				method: "DELETE",
			});
			setEntries((prev) => prev.filter((entry) => entry.date !== targetDate));
			if (targetDate === date) setValues({});
			toast.success(t("wellnessDataDeleted"));
			window.dispatchEvent(new Event("wellness-updated"));
		} catch {
			toast.error(t("wellnessDeleteError"));
		} finally {
			setDeleting(null);
		}
	};

	if (loading) {
		return (
			<Layout>
				<div className="flex items-center justify-center h-[50vh] text-muted-foreground">
					<Loader2 className="w-5 h-5 animate-spin mr-2" />
					{t("loading")}
				</div>
			</Layout>
		);
	}

	if (!config?.enabled) {
		return (
			<Layout>
				<div className="max-w-xl mx-auto px-4 py-8 pb-24 space-y-5">
					<HeartPulse className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
					<div className="text-center">
						<h1 className="font-heading text-xl mb-2">
							{t("wellnessDiaryTitle")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("wellnessUnavailable")}
						</p>
					</div>
					<div className="space-y-3">
						<div>
							<h2 className="font-heading text-base">
								{t("wellnessSelfSetupTitle")}
							</h2>
							<p className="text-xs text-muted-foreground">
								{t("wellnessSelfSetupDescription")}
							</p>
						</div>
						<WellnessFieldPicker
							catalog={catalog}
							selected={selectedFields}
							onToggle={toggleSelectedField}
							showHelp
						/>
						{selectedFields.length === 0 && (
							<p className="text-xs text-amber-400">
								{t("wellnessSelectAtLeastOne")}
							</p>
						)}
						<Button
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-12"
							disabled={activating || selectedFields.length === 0}
							onClick={handleActivate}
						>
							{activating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
							{t("wellnessActivateDiary")}
						</Button>
					</div>
					<Button
						variant="outline"
						className="w-full rounded-sm"
						onClick={() => navigate("/profile")}
					>
						{t("profile")}
					</Button>
				</div>
			</Layout>
		);
	}

	if (config?.consent_required) {
		return (
			<Layout>
				<div className="max-w-xl mx-auto px-4 py-8">
					<div className="flex items-center gap-2 mb-4">
						<ShieldCheck className="w-6 h-6 text-volt" />
						<h1 className="font-heading text-xl">
							{t("wellnessConsentTitle")}
						</h1>
					</div>
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="p-4">
							<p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
								{consentText}
							</p>
						</CardContent>
					</Card>
					<Button
						className="w-full bg-volt hover:bg-volt/90 rounded-sm mt-5 h-12"
						disabled={accepting}
						onClick={handleAcceptConsent}
					>
						{accepting ? (
							<Loader2 className="w-4 h-4 animate-spin mr-2" />
						) : (
							<Check className="w-4 h-4 mr-2" />
						)}
						{t("wellnessAcceptAndStart")}
					</Button>
					<p className="text-xs text-muted-foreground text-center mt-3">
						{t("wellnessConsentProfileHint")}
					</p>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="max-w-xl mx-auto px-4 py-6 pb-24 space-y-5">
				<div className="flex items-center gap-2">
					<HeartPulse className="w-6 h-6 text-volt" />
					<div>
						<h1 className="font-heading text-xl leading-tight">
							{t("wellnessDiaryTitle")}
						</h1>
						<p className="text-xs text-muted-foreground">
							{t("wellnessDiarySubtitle")}
						</p>
					</div>
				</div>

				{/* View switch: compile vs trends */}
				<div className="flex gap-1.5">
					<Button
						variant={view === "form" ? "default" : "secondary"}
						size="sm"
						className={`rounded-sm flex-1 ${view === "form" ? "bg-volt hover:bg-volt/90" : ""}`}
						onClick={() => setView("form")}
					>
						<ClipboardList className="w-4 h-4 mr-1.5" />
						{t("wellnessInsert")}
					</Button>
					<Button
						variant={view === "charts" ? "default" : "secondary"}
						size="sm"
						className={`rounded-sm flex-1 ${view === "charts" ? "bg-volt hover:bg-volt/90" : ""}`}
						onClick={() => setView("charts")}
					>
						<BarChart3 className="w-4 h-4 mr-1.5" />
						{t("wellnessTrends")}
					</Button>
				</div>

				{view === "form" ? (
					<>
						<div className="space-y-2">
							<div className="flex items-center justify-between gap-2">
								<Label className="label-tactical">{t("wellnessDay")}</Label>
								{selectedDayFilled ? (
									<Badge className="rounded-sm bg-green-500/20 text-green-400 border-0 gap-1">
										<CheckCircle2 className="w-3 h-3" />
										{isToday ? t("wellnessTodayFilled") : t("wellnessFilled")}
									</Badge>
								) : (
									<Badge
										variant="outline"
										className="rounded-sm text-muted-foreground"
									>
										{t("wellnessToFill")}
									</Badge>
								)}
							</div>
							<Input
								type="date"
								value={date}
								max={getToday()}
								onChange={(e) => setDate(e.target.value)}
								className="bg-secondary border-transparent rounded-sm h-11"
							/>
							{selectedDayFilled && (
								<p className="text-xs text-muted-foreground">
									{t("wellnessDayAlreadyFilled")}
								</p>
							)}
						</div>

						<div className="space-y-4">
							{activeFields.map((field) => (
								<Card
									key={field.key}
									className="bg-card border-border rounded-sm"
								>
									<CardContent className="p-4 space-y-2">
										<div className="flex items-baseline justify-between gap-2">
											<Label className="text-sm font-medium">
												{field.label}
											</Label>
											{field.unit && (
												<span className="text-xs text-muted-foreground">
													{field.unit}
												</span>
											)}
										</div>
										{field.help && (
											<p className="text-xs text-muted-foreground">
												{field.help}
											</p>
										)}

										{field.type === "scale_1_5" && (
											<div className="grid grid-cols-5 gap-2 pt-1">
												{WELLNESS_SCALE_OPTIONS.map((option) => {
													const active = Number(values[field.key]) === option;
													return (
														<button
															key={option}
															type="button"
															onClick={() =>
																setValue(field.key, active ? "" : option)
															}
															className={`h-11 rounded-sm border text-sm font-heading transition-colors ${
																active
																	? "bg-volt text-white border-volt"
																	: "bg-secondary border-border text-muted-foreground hover:border-volt/50"
															}`}
														>
															{option}
														</button>
													);
												})}
											</div>
										)}

										{field.type === "number" && (
											<Input
												type="number"
												inputMode="decimal"
												min={field.min}
												max={field.max}
												step={field.step || 1}
												value={values[field.key] ?? ""}
												onChange={(e) => setValue(field.key, e.target.value)}
												className="bg-secondary border-transparent rounded-sm h-11"
												placeholder="—"
											/>
										)}

										{field.type === "text" && (
											<Textarea
												value={values[field.key] ?? ""}
												onChange={(e) => setValue(field.key, e.target.value)}
												className="bg-secondary border-transparent rounded-sm min-h-[80px]"
												placeholder={t("wellnessWriteHere")}
											/>
										)}
									</CardContent>
								</Card>
							))}
						</div>

						<Button
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-12"
							disabled={saving}
							onClick={handleSave}
						>
							{saving ? (
								<Loader2 className="w-4 h-4 animate-spin mr-2" />
							) : selectedDayFilled ? (
								<PencilLine className="w-4 h-4 mr-2" />
							) : (
								<Check className="w-4 h-4 mr-2" />
							)}
							{selectedDayFilled ? t("wellnessUpdate") : t("save")}
						</Button>

						{entries.length > 0 && (
							<div className="space-y-2 pt-2">
								<Label className="label-tactical">
									{t("wellnessRecordedDays")}
								</Label>
								<div className="space-y-1.5">
									{[...entries]
										.filter(entryHasData)
										.sort((a, b) =>
											String(b.date).localeCompare(String(a.date)),
										)
										.slice(0, 30)
										.map((entry) => {
											const filled = activeFields.filter(
												(field) =>
													entry.values?.[field.key] !== undefined &&
													entry.values?.[field.key] !== null &&
													entry.values?.[field.key] !== "",
											);
											const isSelected = entry.date === date;
											return (
												<div
													key={entry.date}
													className={`rounded-sm border px-3 py-2 transition-colors ${
														isSelected
															? "border-volt/60 bg-volt/5"
															: "border-border bg-card"
													}`}
												>
													<div className="flex items-center justify-between gap-2">
														<button
															type="button"
															onClick={() => setDate(entry.date)}
															className="min-w-0 flex-1 text-left group"
														>
															<div className="flex items-center gap-2">
																<span className="text-sm font-medium group-hover:text-volt transition-colors">
																	{entry.date}
																</span>
																{isSelected && (
																	<span className="text-[10px] uppercase tracking-wider text-volt">
																		{t("wellnessEditing")}
																	</span>
																)}
															</div>
															{filled.length > 0 && (
																<p className="text-xs text-muted-foreground mt-0.5 truncate">
																	{filled
																		.map(
																			(field) =>
																				`${field.label}: ${formatWellnessValue(
																					field,
																					entry.values[field.key],
																				)}`,
																		)
																		.join(" · ")}
																</p>
															)}
														</button>
														<div className="flex items-center gap-1 shrink-0">
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 rounded-sm text-muted-foreground hover:text-volt"
																title={t("edit")}
																onClick={() => {
																	setDate(entry.date);
																	setView("form");
																}}
															>
																<PencilLine className="w-4 h-4" />
															</Button>
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive"
																title={t("delete")}
																disabled={deleting === entry.date}
																onClick={() => handleDeleteDay(entry.date)}
															>
																{deleting === entry.date ? (
																	<Loader2 className="w-4 h-4 animate-spin" />
																) : (
																	<Trash2 className="w-4 h-4" />
																)}
															</Button>
														</div>
													</div>
												</div>
											);
										})}
								</div>
							</div>
						)}
					</>
				) : (
					<div className="pt-1">
						<WellnessCharts
							entries={entries}
							catalog={catalog}
							fields={config?.fields || []}
							emptyLabel={t("wellnessAthleteTrendsEmpty")}
						/>
					</div>
				)}
			</div>
		</Layout>
	);
}
